import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { guestSession } from '../guestSession.js';
import type { ArenaSocket } from '../realtime.js';
import { chatMaxLength, checkChatMessage } from '../../shared/chatRules.js';
import { playChatSound } from '../audio/sounds.js';
import { EmojiPicker, insertAtCursor } from './EmojiPicker.js';
import { SendButton } from './SendButton.js';
import type { ArenaResult, ChatMessage, Role } from '../../shared/protocol.js';

/**
 * Match chat (suunnitelma.md section 25): opened from a button, shown as a side panel on
 * desktop and a bottom sheet on mobile. Unread messages are counted while it is closed.
 */
export function ChatPanel(props: { socket: ArenaSocket; gameId: number; myId: number; role: Role; onIncoming?: () => void }) {
  const { socket, gameId } = props;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const load = () =>
      socket.emit('chat:history', (r: ArenaResult<{ messages: ChatMessage[] }>) => {
        if (r.ok) setMessages(r.messages);
      });
    const onMessage = (p: { gameId: number; message: ChatMessage }) => {
      if (p.gameId !== gameId) return;
      setMessages((list) => (list.some((m) => m.id === p.message.id) ? list : [...list, p.message]));
      if (p.message.userId !== props.myId) {
        if (!openRef.current) setUnread((n) => n + 1);
        props.onIncoming?.();
        playChatSound('chatMessage');
      }
    };
    load();
    socket.on('chat:message', onMessage);
    socket.on('connect', load);
    return () => {
      socket.off('chat:message', onMessage);
      socket.off('connect', load);
    };
  }, [socket, gameId]);

  useEffect(() => {
    if (open) setUnread(0);
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [open, messages.length]);

  const send = (e: Event) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    const rule = checkChatMessage(body, props.role);
    if (rule) return setError(t.arena.errors[rule]);
    setError(null);
    socket.emit('chat:send', { text: body }, (r: ArenaResult) => {
      if (r.ok) setText('');
      else setError(t.arena.errors[r.error]);
    });
  };

  return (
    <>
      <button class="btn btn-small chat-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        💬 {t.chat.title}
        {unread > 0 && <span class="unread">{unread}</span>}
      </button>
      {open && (
        <aside class="chat-panel" aria-label={t.chat.title}>
          <div class="chat-head">
            <strong>{t.chat.title}</strong>
            <button class="btn btn-ghost btn-small" onClick={() => setOpen(false)}>
              {t.chat.close}
            </button>
          </div>
          <div class="chat-list" ref={listRef}>
            {messages.length === 0 && <p class="muted chat-empty">{t.chat.empty}</p>}
            {messages.map((m) => (
              <div class={`chat-msg ${m.userId === props.myId ? 'mine' : ''}`} key={m.id}>
                <div class="chat-meta">
                  {m.userId === props.myId ? t.chat.you : m.displayName} ·{' '}
                  {new Date(m.sentAt).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div class="chat-text">{m.text}</div>
              </div>
            ))}
          </div>
          {error && <p class="error">{error}</p>}
          {guestSession.guest ? (
            <p class="chat-readonly">{t.guest.readOnly}</p>
          ) : (
            <form class="chat-form" onSubmit={send}>
              <input
                ref={inputRef}
                value={text}
                onInput={(e) => setText(e.currentTarget.value)}
                placeholder={t.chat.placeholder}
                maxLength={chatMaxLength(props.role)}
                aria-label={t.chat.placeholder}
              />
              <EmojiPicker onPick={(emoji) => setText(insertAtCursor(inputRef.current, text, emoji))} />
              <SendButton label={t.chat.send} disabled={!text.trim()} />
            </form>
          )}
        </aside>
      )}
    </>
  );
}
